// src/components/EmailVerification.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { applyActionCode, checkActionCode, auth } from '../utils/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';

const functions = getFunctions();

function EmailVerification() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      const mode = searchParams.get('mode');
      const oobCode = searchParams.get('oobCode');

      console.log('🔍 Email verification page loaded');
      console.log('Mode:', mode);
      console.log('Has oobCode:', !!oobCode);

      if (mode !== 'verifyEmail') {
        setStatus('error');
        setErrorMessage('Invalid action. This link may have been used for a different purpose.');
        return;
      }

      if (!oobCode) {
        setStatus('error');
        setErrorMessage('Invalid verification link. Please request a new verification email.');
        return;
      }

      try {
        const actionInfo = await checkActionCode(auth, oobCode);
        console.log('✅ Action code is valid');
        console.log('📧 Email being verified:', actionInfo.data.email);

        await applyActionCode(auth, oobCode);
        console.log('✅ Email verified successfully');

        // Send welcome email now that verification is complete
        try {
          console.log('📤 Sending welcome email...');
          const sendWelcomeEmail = httpsCallable(functions, 'sendWelcomeEmail');
          await sendWelcomeEmail({ email: actionInfo.data.email });
          console.log('✅ Welcome email sent');
        } catch (welcomeError) {
          console.error('⚠️ Failed to send welcome email:', welcomeError);
        }

        setStatus('success');

        setTimeout(() => {
          navigate('/login', { 
            state: { message: 'Email verified successfully! Please sign in.' }
          });
        }, 3000);

      } catch (error) {
        console.error('❌ Verification error:', error);
        setStatus('error');

        switch (error.code) {
          case 'auth/expired-action-code':
            setErrorMessage('This verification link has expired. Please request a new one.');
            break;
          case 'auth/invalid-action-code':
            setErrorMessage('This verification link is invalid or has already been used.');
            break;
          case 'auth/user-disabled':
            setErrorMessage('This account has been disabled.');
            break;
          case 'auth/user-not-found':
            setErrorMessage('No account found for this verification link.');
            break;
          default:
            setErrorMessage('Failed to verify email. Please try again or request a new verification link.');
        }
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f3f4f6',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        padding: '48px',
        maxWidth: '450px',
        width: '90%',
        textAlign: 'center'
      }}>
        <div style={{
          backgroundColor: '#243665',
          color: 'white',
          padding: '16px 32px',
          borderRadius: '8px',
          marginBottom: '32px',
          display: 'inline-block'
        }}>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 700 }}>⚽ Pitchallo</h1>
        </div>

        {status === 'verifying' && (
          <>
            <div style={{
              width: '48px',
              height: '48px',
              border: '4px solid #e5e7eb',
              borderTopColor: '#243665',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 24px'
            }} />
            <h2 style={{ color: '#1f2937', marginBottom: '8px' }}>Verifying your email...</h2>
            <p style={{ color: '#6b7280' }}>Please wait while we verify your email address.</p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </>
        )}

        {status === 'success' && (
          <>
            <div style={{
              width: '64px',
              height: '64px',
              backgroundColor: '#10b981',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
            <h2 style={{ color: '#1f2937', marginBottom: '8px' }}>Email Verified!</h2>
            <p style={{ color: '#6b7280', marginBottom: '24px' }}>
              Your email has been verified successfully.
            </p>
            <p style={{ color: '#9ca3af', fontSize: '14px' }}>
              Redirecting to login...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={{
              width: '64px',
              height: '64px',
              backgroundColor: '#ef4444',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </div>
            <h2 style={{ color: '#1f2937', marginBottom: '8px' }}>Verification Failed</h2>
            <p style={{ color: '#6b7280', marginBottom: '24px' }}>
              {errorMessage}
            </p>
            <button
              onClick={() => navigate('/login')}
              style={{
                backgroundColor: '#243665',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Go to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default EmailVerification;
